import { useParams, useNavigate, Link } from "react-router-dom";
import { Fragment, useEffect, useState } from "react";
import axios from "axios";
import { Badge } from "react-bootstrap";
import Sidebar from "../../../components/sidebar/Sidebar";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { getPackageColor, getPackageName, getPackagePrice, getPackageProfit, getPackageTitle } from "../../../utils/packages";
import "../../accounts/view/ViewAccountPage.css";
import { Money, Username } from "../../../utils/privacyMask";
import { PAYMENT_ICONS, PAYMENT_COLORS } from "../../../utils/paymentMethods";

function InfoItem({ icon, label, value, accent }) {
  return (
    <div className="info-item">
      <div className="info-label"><i className={`bi ${icon}`} />{label}</div>
      <div className={`info-value ${!value && value !== 0 ? 'muted' : ''}`} style={accent ? { color: accent, fontWeight: 600 } : {}}>
        {value !== undefined && value !== null && value !== '' ? value : 'Not provided'}
      </div>
    </div>
  );
}

export default function ViewSubscriptionPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState(null);
  const [account, setAccount]           = useState(null);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const subRes = await axios.get(`${process.env.REACT_APP_API_URL}/subscription/${id}`, { headers });
        setSubscription(subRes.data);
        if (subRes.data.accountID) {
          const accRes = await axios.get(`${process.env.REACT_APP_API_URL}/account/${subRes.data.accountID}`, { headers });
          setAccount(accRes.data);
        }
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load subscription");
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <LoadingSpinner />;
  if (error || !subscription) return (
    <div className="view-page">
      <div className="view-panel">
        <div style={{ padding: 24, color: '#f87171' }}>
          <i className="bi bi-exclamation-triangle-fill me-2" />{error || 'Subscription not found'}
        </div>
      </div>
    </div>
  );

  const expiryDays = Math.round((Date.now() - Number(subscription.expireDate)) / 86400000) - 1;

  return (
    <>
      <Sidebar title="View Subscription" />

      <div className="view-page">

        {/* ── Header ── */}
        <div className="view-header">
          <div className="view-header-left">
            <div className="view-avatar" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
              <i className="bi bi-bookmark-star-fill" />
            </div>
            <div>
              <div className="view-title">{subscription.username ? <Username value={subscription.username} /> : 'Subscription'}</div>
              <div className="view-subtitle">
                {account?.account?.username && <><Username value={account.account.username} /> · </>}
                ID: {subscription._id}
              </div>
            </div>
          </div>
          <Link to={`/subscription/edit/${subscription._id}`} className="btn-edit-account">
            <i className="bi bi-pencil-square" /> Edit Subscription
          </Link>
        </div>

        {/* ── Stat cards ── */}
        <div className="view-stats">
          <div className="view-stat-card" style={{ '--accent': subscription.active ? '#10b981' : '#f87171', '--glow': subscription.active ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)' }}>
            <div className="view-stat-label">Status</div>
            <div className="view-stat-value" style={{ fontSize: '1.2rem' }}>{subscription.active ? 'Active' : 'Inactive'}</div>
            <i className={`bi ${subscription.active ? 'bi-check-circle-fill' : 'bi-x-circle-fill'} view-stat-icon`} />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#34d399', '--glow': 'rgba(52,211,153,0.3)' }}>
            <div className="view-stat-label">Paid Price</div>
            <div className="view-stat-value"><Money value={subscription.paidPrice || 0} /></div>
            <i className="bi bi-cash-stack view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': '#38bdf8', '--glow': 'rgba(56,189,248,0.3)' }}>
            <div className="view-stat-label">Profit</div>
            <div className="view-stat-value"><Money value={subscription.profitReceived || 0} /></div>
            <i className="bi bi-graph-up-arrow view-stat-icon" />
          </div>
          <div className="view-stat-card" style={{ '--accent': expiryDays < 0 ? '#10b981' : '#f87171', '--glow': expiryDays < 0 ? 'rgba(16,185,129,0.3)' : 'rgba(248,113,113,0.3)' }}>
            <div className="view-stat-label">Expires</div>
            <div className="view-stat-value" style={{ fontSize: '1.1rem' }}>{Math.abs(expiryDays)}d {expiryDays < 0 ? 'left' : 'ago'}</div>
            <i className="bi bi-calendar-x view-stat-icon" />
          </div>
        </div>

        {/* ── Subscription info ── */}
        <div className="view-panel">
          <div className="view-panel-header">
            <h6 className="view-panel-title">
              <i className="bi bi-bookmark-star-fill" style={{ color: '#f59e0b' }} />
              Subscription Details
            </h6>
            {getPackageTitle(subscription.package) ? (
              <Badge bg="" style={{ background: getPackageColor(subscription.package), fontSize: '0.72rem' }}>
                {getPackageTitle(subscription.package)} – {getPackageName(subscription.package)}
              </Badge>
            ) : (
              <Badge bg="" style={{ background: 'var(--color-surface-2)', color: 'var(--color-subtle)', fontSize: '0.72rem', border: '1px solid var(--color-border)' }}>
                No package assigned
              </Badge>
            )}
          </div>
          <div className="info-grid">
            <InfoItem icon="bi-person-fill"    label="Account"         value={account?.account?.username ? <Username value={account.account.username} /> : null} />
            <InfoItem icon="bi-person-badge"   label="Username"        value={subscription.username ? <Username value={subscription.username} /> : null} />
            <InfoItem icon="bi-key"            label="Password"        value={subscription.password} />
            <InfoItem icon="bi-coin"           label="Credits"         value={subscription.creditCost} />
            <InfoItem icon="bi-cash-stack"     label="Paid Price"      value={<Money value={subscription.paidPrice || 0} />}          accent="#34d399" />
            <InfoItem icon="bi-graph-up-arrow" label="Profit Received" value={<Money value={subscription.profitReceived || 0} />}      accent="#38bdf8" />
            <InfoItem icon="bi-tag"            label="Renewal Price"   value={getPackageTitle(subscription.package) ? <Money value={getPackagePrice(subscription.package)} /> : null} />
            <InfoItem icon="bi-tag-fill"       label="Renewal Profit"  value={getPackageTitle(subscription.package) ? <Money value={getPackageProfit(subscription.package)} /> : null} />
            <InfoItem icon="bi-calendar-plus"  label="Start Date"      value={new Date(Number(subscription.createdDate)).toLocaleDateString()} />
            <InfoItem icon="bi-calendar-x"     label="Expire Date"     value={new Date(Number(subscription.expireDate)).toLocaleDateString()} />
            <InfoItem
              icon={PAYMENT_ICONS[subscription.paymentMethod] || 'bi-credit-card'}
              label="Payment Method"
              value={subscription.paymentMethod ? subscription.paymentMethod.charAt(0).toUpperCase() + subscription.paymentMethod.slice(1) : null}
              accent={subscription.paymentMethod ? PAYMENT_COLORS[subscription.paymentMethod] : undefined}
            />
            <InfoItem icon="bi-journal-text"   label="Notes"           value={subscription.notes} />
          </div>
        </div>

        {/* ── Additional accounts ── */}
        {subscription.additionalAccounts?.length > 0 && (
          <div className="view-panel">
            <div className="view-panel-header">
              <h6 className="view-panel-title">
                <i className="bi bi-people-fill" style={{ color: '#a78bfa' }} />
                Additional Accounts
              </h6>
              <span className="view-panel-count">{subscription.additionalAccounts.length}</span>
            </div>
            <div className="info-grid">
              {subscription.additionalAccounts.map(([u, p], i) => (
                <Fragment key={i}>
                  <InfoItem icon="bi-person-badge" label={`Account ${i + 1} — Username`} value={<Username value={u} />} />
                  <InfoItem icon="bi-key" label={`Account ${i + 1} — Password`} value={p} />
                </Fragment>
              ))}
            </div>
          </div>
        )}

        {/* ── Actions ── */}
        <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
          <button className="btn-edit-account" onClick={() => window.history.back()}>
            <i className="bi bi-arrow-left" /> Back
          </button>
          {account && (
            <button className="btn-edit-account" onClick={() => navigate(`/account/view/${account.account._id}`)}>
              <i className="bi bi-person-fill" /> View Account
            </button>
          )}
        </div>

      </div>
    </>
  );
}
